import { useSyncExternalStore } from 'react';
import { REPLAY_PACES, type ReplayPlayer } from '../game/replay';
import type { OnlineStatus } from '../game/session';
import type { RoomController } from '../net/useRoom';

interface Props {
  status: OnlineStatus;
  /** Leaves the room (or the replay) and goes back to the menu. */
  onLeave: () => void;
  /** The room controls, for the replay on the table. */
  room: RoomController;
  /** Who sits at a seat, for the AI standing in for them. */
  seatName: (seat: number) => string;
}

/** What the server-side game is waiting for, shown over the table and the arena alike. */
export function OnlineBanner({ status, onLeave, room, seatName }: Props) {
  if (room.replay) return <ReplayBanner player={room.replay} onLeave={onLeave} />;
  const text = bannerText(status, seatName);
  if (!text) return null;
  const over = status.ended !== null || status.connection === 'closed' || status.desynced;
  return (
    <div
      className={`online-banner${over ? ' over' : ''}`}
      role="status"
      data-testid="online-banner"
    >
      <span>{text}</span>
      {/* The seat's token is kept, so a reload takes the seat up again where the server has it. */}
      {status.desynced && status.ended === null && (
        <button data-testid="online-reload" onClick={() => window.location.reload()}>
          Reload
        </button>
      )}
      {over && <button onClick={onLeave}>Back to menu</button>}
    </div>
  );
}

function bannerText(status: OnlineStatus, seatName: (seat: number) => string): string | null {
  if (status.ended === 'abandoned') return 'Everyone else has left the game.';
  if (status.desynced && status.ended === null)
    return 'This screen lost track of the game. Reload to pick it up again: your seat is kept.';
  if (status.ended === 'expired') return 'The room expired.';
  if (status.connection === 'closed')
    return status.notice ?? 'The connection to the server is gone.';
  if (status.connection === 'reconnecting') return 'Connection lost, trying to get back in…';
  if (status.paused.length > 0)
    return `Waiting for ${status.paused.map((dropped) => dropped.name).join(' and ')} to reconnect…`;
  const parts: string[] = [];
  if (status.spectator) parts.push('Watching');
  // The panes cover this player's own seat; the banner keeps naming whom else the AI plays for.
  const away = status.standIns.map(seatName);
  if (away.length > 0) parts.push(`The AI plays for ${away.join(' and ')} until they are back`);
  if (status.notice) parts.push(status.notice);
  if (status.clock) parts.push(clockText(status.clock));
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** "Your move · 0:27": the seconds come from the server's last word, never from a local timer. */
function clockText(clock: NonNullable<OnlineStatus['clock']>): string {
  return `${clock.mine ? 'Your move' : `${clock.name} to move`} · ${formatSeconds(clock.secondsLeft)}`;
}

export function formatSeconds(total: number): string {
  const seconds = Math.max(0, total);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** Playback controls for the replay on the table: play, pause, one step at a time, the pace. */
function ReplayBanner({ player, onLeave }: { player: ReplayPlayer; onLeave: () => void }) {
  const view = useSyncExternalStore(
    (listener) => player.onChange(listener),
    () => player.getSnapshot(),
    () => player.getSnapshot(),
  );
  return (
    <div
      className={`online-banner replay${view.done ? ' over' : ''}`}
      role="status"
      data-testid="online-banner"
    >
      <span data-testid="replay-progress">
        {view.done ? `Replay over · ${view.outcome}` : `Replay · ${view.index} / ${view.total}`}
      </span>
      {!view.done && (
        <>
          <button
            onClick={() => (view.playing ? player.pause() : player.play())}
            data-testid="replay-toggle"
          >
            {view.playing ? 'Pause' : 'Play'}
          </button>
          <button onClick={() => player.step()} disabled={view.playing} data-testid="replay-step">
            Step
          </button>
          <div className="choice" role="radiogroup" aria-label="Pace">
            {REPLAY_PACES.map((pace) => (
              <button
                key={pace}
                role="radio"
                aria-checked={view.pace === pace}
                className={view.pace === pace ? 'active' : ''}
                onClick={() => player.setPace(pace)}
              >
                {pace}×
              </button>
            ))}
          </div>
        </>
      )}
      <button onClick={onLeave}>Back to menu</button>
    </div>
  );
}
